import React, { useEffect, useRef, useState } from "react";
import {
    Container,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Divider,
    Grid,
    IconButton,
    Box,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import { Add, Edit, Delete, Close } from "@mui/icons-material";
import authService from "../Auth/AuthService";

export default function FeesStructurePage({ masterId }) {
    const [feesStructures, setFeesStructures] = useState([]);
    const [formData, setFormData] = useState({
        id: null,
        schoolId: masterId,
        annualFee: "",
        vehicleFee: '',
        standard: "",
        branch: "",
    });
    const [isEditing, setIsEditing] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const hasFetched = useRef(false);
    const [isDisabled, setIsDisabled] = useState(false);

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;
        fetchFeesStructures();
    }, []);

    const fetchFeesStructures = async () => {
        try {
            const url = authService.pathUrl('GET', '/fees/structure');
            const response = await authService.getAuth(url);
            setFeesStructures(response);
        } catch (error) {
            console.error("Error fetching fees structures:", error);
        }
    };

    const sortedFeesStructures = [...feesStructures].sort((a, b) => {
        const aNum = parseInt(a.standard, 10);
        const bNum = parseInt(b.standard, 10);
        return aNum - bNum;
    });

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsDisabled(true);
        try {
            const url = authService.pathUrl('PUTPOST', '/fees/structure');
            if (isEditing) {
                await authService.putAuth(`${url}/${formData.id}`, formData);
            } else {
                await authService.postAuth(url, formData);
            }
            await fetchFeesStructures();
            resetForm();
            setDialogOpen(false);
            setIsDisabled(false);
        } catch (error) {
            console.error("Error saving fees structure:", error);
            setIsDisabled(false);
        }
    };

    const handleEdit = (structure) => {
        setFormData(structure);
        setIsEditing(true);
        setDialogOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this fees structure?")) {
            try {
                const url = authService.pathUrl('DELETE', `/fees/structure/${id}`);
                await authService.deleteAuth(url);
                await fetchFeesStructures();
            } catch (error) {
                console.error("Error deleting fees structure:", error);
            }
        }
    };

    const handleAddClick = () => {
        resetForm();
        setDialogOpen(true);
    };

    const resetForm = () => {
        setFormData({
            id: null,
            schoolId: masterId?.masterId || masterId,
            annualFee: "",
            vehicleFee: '',
            standard: "",
            branch: '',
        });
        setIsEditing(false);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        resetForm();
    };

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" gutterBottom fontWeight="bold" color="primary">
                    Fees Structure Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleAddClick}
                    sx={{ bgcolor: '#3f2d01', borderRadius: 5, fontWeight: 'bold' }}
                >
                    Add New
                </Button>
            </Box>

            {/* --- Dialog for Add/Edit --- */}
            <Dialog 
                open={dialogOpen} 
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" component="span">
                        {isEditing ? "Update Fees Structure" : "Add Fees Structure"}
                    </Typography>
                    <IconButton onClick={handleCloseDialog} sx={{ color: '#cbc7c7' }}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        <TextField
                            label="Standard (1st/2nd/3rd/4th..)"
                            name="standard"
                            value={formData.standard}
                            onChange={handleChange}
                            fullWidth
                            margin="normal"
                            required
                        />
                        <TextField
                            label="Annual Fee"
                            name="annualFee"
                            type="number"
                            value={formData.annualFee}
                            onChange={handleChange}
                            fullWidth
                            margin="normal"
                            required
                        />
                        <TextField
                            label="Vehicle Fee"
                            name="vehicleFee"
                            type="number"
                            value={formData.vehicleFee}
                            onChange={handleChange}
                            fullWidth
                            margin="normal"
                            required
                        />
                        {parseInt(formData.standard) > 10 ? (
                            <TextField
                                label="Branch"
                                name="branch"
                                placeholder="Lower/Science/Commerce/Arts"
                                value={formData.branch}
                                onChange={handleChange}
                                fullWidth
                                margin="normal"
                                required
                            />
                        ) : (
                            <TextField
                                label="Branch"
                                name="branch"
                                placeholder="Lower/Science/Commerce/Arts"
                                value={formData.branch}
                                onChange={handleChange}
                                fullWidth
                                margin="normal"
                            />
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={handleCloseDialog} variant="outlined" sx={{ borderRadius: 5 }}>
                            Cancel
                        </Button>
                        <Button 
                            type="submit" 
                            variant="contained" 
                            sx={{ bgcolor: '#3f2d01', borderRadius: 5, fontWeight: 'bold' }}
                            disabled={isDisabled}
                        >
                            {isDisabled ? (
                                <CircularProgress size={20} sx={{ color: '#fff' }} />
                            ) : (
                                isEditing ? "Update" : "Add"
                            )}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* --- Table --- */}
            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom color="#3f2d01" textAlign={'center'}>
                        Existing Fees Structures
                    </Typography>
                    <Divider />
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>ID</TableCell>
                                <TableCell>Created By</TableCell>
                                <TableCell>Standard</TableCell>
                                <TableCell>Annual Fee</TableCell>
                                <TableCell>Vehicle Fee</TableCell>
                                <TableCell>Branches</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Array.isArray(sortedFeesStructures) && sortedFeesStructures
                                .map((structure) => (
                                    <TableRow key={structure.id}>
                                        <TableCell>{structure.id}</TableCell>
                                        <TableCell>{structure.createdBy}</TableCell>
                                        <TableCell>{structure.standard}</TableCell>
                                        <TableCell>{structure.annualFee}</TableCell>
                                        <TableCell>{structure.vehicleFee}</TableCell>
                                        <TableCell>{structure.branch}</TableCell>
                                        <TableCell>
                                            <IconButton
                                                onClick={() => handleEdit(structure)}
                                                title="Update"
                                                sx={{ mr: 1 }}
                                            >
                                                <Edit sx={{ border: "2px solid", borderRadius: "10px" }} />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleDelete(structure.id)}
                                                title="Delete"
                                                color="error"
                                            >
                                                <Delete sx={{ border: "2px solid", borderRadius: "10px" }} />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </Box>
    );
}